import { Check, Plus, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Pick the people on a project, as chips with a menu to add more.
 *
 * Made a component of its own because the same control now has to exist in
 * two places, and the second place is the one that matters: this picker lived
 * only inside a project, under Last activity, so nothing in the create dialog
 * suggested a project could have people at all. Five projects were created in
 * a row without a single name on any of them - not a preference, just a
 * control nobody had reason to look for.
 *
 * Works on ids rather than on the resolved objects the project endpoint
 * returns, since at creation there is no project yet and nothing to resolve
 * against: the caller holds a list of ids and hands it straight to the API.
 *
 * ProjectStatusBar keeps its own copy for now. It is in daily use and swapping
 * it for this on the same day would be risking a working page for tidiness;
 * the two should be merged once this one has been seen to behave.
 */
export function PeoplePicker({ allUsers, selectedIds, onToggle, s }) {
  // Both sides normalised to strings. Compared without it the tick never
  // appears where the types differ, so the menu shows nobody as chosen and
  // clicking a name that is already there silently removes them.
  const selected = new Set((selectedIds || []).map(String));
  const chosen = (allUsers || []).filter((u) => selected.has(String(u._id)));

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="people-picker">
      {chosen.map((u) => (
        <span
          key={u._id}
          className="inline-flex items-center gap-1.5 pl-1 pr-1 py-1 rounded-full bg-slate-50 border border-slate-200 text-sm max-w-full"
          data-testid={`picked-person-${u._id}`}
        >
          <span
            className="w-5 h-5 rounded-full text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
            style={{ backgroundColor: u.color || "#3730A3" }}
          >
            {(u.display_name || "?").charAt(0).toUpperCase()}
          </span>
          <span className="text-slate-700 truncate">{u.display_name}</span>
          {/* 24px rather than the bare icon: a thumb aiming at a small X
              inside a chip hits the chip instead. */}
          <button
            type="button"
            onClick={() => onToggle(u._id)}
            aria-label={`${s("proj.waitingClear")} \u00b7 ${u.display_name}`}
            data-testid={`unpick-person-${u._id}`}
            className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </span>
      ))}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* type="button": this sits inside the create form, and a button
              without it submits the form on click - opening the menu would
              have created the project. */}
          <button
            type="button"
            data-testid="pick-person-btn"
            aria-label={s("proj.people")}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-dashed border-slate-300 text-sm text-slate-400 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {chosen.length === 0 ? s("proj.noPeople") : ""}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {(allUsers || []).map((u) => (
            <DropdownMenuItem
              key={u._id}
              onSelect={(e) => {
                // Keeps the menu open: picking three people out of a menu that
                // shuts after each one means opening it three times.
                e.preventDefault();
                onToggle(u._id);
              }}
              data-testid={`pick-toggle-${u._id}`}
              className="flex items-center gap-2 cursor-pointer"
            >
              <span
                className="w-5 h-5 rounded-full text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
                style={{ backgroundColor: u.color || "#3730A3" }}
              >
                {(u.display_name || "?").charAt(0).toUpperCase()}
              </span>
              <span className="flex-1 truncate">{u.display_name}</span>
              {selected.has(String(u._id)) && (
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
